import { LitElement, html } from 'lit';
import { state, customElement } from 'lit/decorators.js';
import { GoonAPIClient } from '../client/ApiClient';
import { API_ENDPOINT } from '../constants';
import AppAnalytics from '../client/AnalyticsClient';

const api = new GoonAPIClient(API_ENDPOINT);

@customElement('search-bar')
export class SearchBar extends LitElement {
  createRenderRoot() { return this }
  @state() query = '';
  @state() loading = false;
  @state() results = { channels: [], users: [], posts: [] };
  cache = new Map();
  timeout;

  static styles = [];

  onInput(e: InputEvent) {
    const value = (e.target as HTMLInputElement).value;
    this.query = value;
    clearTimeout(this.timeout);

    if (!value || value.length < 3) {
      this.results = { channels: [], users: [], posts: [] };
      return;
    }

    this.timeout = setTimeout(async () => {
      if (this.cache.has(value)) {
        this.results = this.cache.get(value);
        return;
      }

      this.loading = true;
      try {
        const json = await api.search(value);
        this.results = json;
        this.cache.set(value, json);
      } catch (err) {
        console.error("Search error", err);
      } finally {
        this.loading = false;
      }

      AppAnalytics.getPlausible()?.trackEvent('search', {
        props: {
          query: value,
        }
      })
    }, 300);
  }

  render() {
    return html`
      <div class="relative w-md">
        <input class="input input-md w-full" placeholder="Search..."
               @input=${e => this.onInput(e)} />
        ${this.query ? html`
          <div class="absolute z-50 mt-1 bg-base-200 border border-base-300 rounded w-full shadow-lg p-2 space-y-4">
            ${this.loading
              ? html`<div class="space-y-2">${Array.from({ length: 3 }).map(() => html`<div class="skeleton h-4 w-full rounded bg-base-300"></div>`)}</div>`
              : html`
                ${this.results.channels.length > 0 ? html`
                  <div>
                    <div class="text-sm font-bold text-base-content/70 mb-1">Channels</div>
                    <div class="flex gap-2 overflow-x-auto pb-2">
                      ${this.results.channels.map(c => html`<a href="/channel/${c.slug}" class="badge badge-lg badge-outline whitespace-nowrap">#${c.name}</a>`)}
                    </div>
                  </div>` : null}

                ${this.results.users.length > 0 ? html`
                  <div>
                    <div class="text-sm font-bold text-base-content/70 mb-1">Users</div>
                    <div class="flex gap-2 overflow-x-auto pb-2">
                      ${this.results.users.map(u => html`
                        <a href="/creator/${u.id}" class="flex items-center gap-2 text-sm">
                          <img class="w-6 h-6 rounded-full" src="${u.avatar}" alt="${u.name}" />
                          <span>${u.name}</span>
                        </a>`)}
                    </div>
                  </div>` : null}

                ${this.results.posts.length > 0 ? html`
                  <div>
                    <div class="text-sm font-bold text-base-content/70 mb-1">Posts</div>
                    <div class="space-y-2 max-h-48 overflow-y-auto">
                      ${this.results.posts.map(p => html`
                        <a href="/post/${p.id}" class="flex items-center gap-2 hover:bg-base-300 px-2 py-1 rounded">
                          ${p.media_urls?.[0] ? html`
                            <img src="${p.media_urls[0]}" class="w-12 h-12 object-cover rounded" alt="post thumbnail" />
                          ` : html`<div class="w-12 h-12 rounded bg-base-300 flex items-center justify-center text-xs">No media</div>`}
                          <span class="text-sm font-medium line-clamp-2">${p.title}</span>
                        </a>
                      `)}
                    </div>
                  </div>` : null}
              `}
          </div>
        ` : null}
      </div>
    `;
  }
}
